<div class="hs-dialog">

  <!-- Header -->
  <div class="hs-header">
    <div class="hs-title-wrap">
      <div class="hs-title-icon">
        <mat-icon>event_repeat</mat-icon>
      </div>
      <div class="hs-title-text">
        <h2 class="hs-title">Shift harvest</h2>
        <div class="hs-subtitle">Move scheduled tasks forward or back in time</div>
      </div>
    </div>
    <button mat-icon-button mat-dialog-close class="hs-close" [disabled]="formSubmitted" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Body -->
  <form id="shiftHarvest" class="hs-body" (ngSubmit)="submit()">

    <!-- Left: form -->
    <div class="hs-col-form">

      <!-- Harvest (preselected, read-only) -->
      @if (!needToPickHarvest && selectedHarvests[0]) {
        <div class="hs-field">
          <label class="hs-label">Harvest</label>
          <div class="hs-harvest-pill">
            <mat-icon>local_florist</mat-icon>
            <span class="hs-harvest-name">{{selectedHarvests[0].name}}</span>
          </div>
        </div>
      }

      <!-- Harvest (multi-select) -->
      @if (needToPickHarvest) {
        <div class="hs-field">
          <label class="hs-label">Harvest(s) <span class="hs-req">*</span></label>
          <mat-form-field appearance="outline" class="hs-mat full-width">
            <mat-select [(ngModel)]="selectedHarvests" (selectionChange)="harvestChange($event.value)" multiple name="harvest" #harvestSelect="ngModel" placeholder="Select harvest(s)">
              <mat-select-trigger>
                {{allSelected ? 'All harvests' : ''}}
                {{!allSelected && selectedHarvests.length > 0 ? selectedHarvests[0].name : ''}}
                @if (!allSelected && selectedHarvests?.length > 1) {
                  <span class="hs-additional">
                    (+{{selectedHarvests.length - 1}} {{selectedHarvests?.length === 2 ? 'other' : 'others'}})
                  </span>
                }
              </mat-select-trigger>
              <mat-option disabled="disabled" class="hs-filter-option">
                <button type="button" class="hs-mini-btn" (click)="selectAllHarvests(harvestSelect, harvests)">Select all</button>
                <button type="button" class="hs-mini-btn ghost" (click)="deselectAllHarvests(harvestSelect)">Clear</button>
              </mat-option>
              @for (h of harvests; track h) {
                <mat-option [value]="h">{{h.name}}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      }

      <!-- Days to shift -->
      <div class="hs-field">
        <label class="hs-label">Days to shift <span class="hs-req">*</span></label>
        <div class="hs-stepper">
          <button type="button" class="hs-step-btn" (click)="numberOfDays = (numberOfDays || 0) - 1" aria-label="Decrease">
            <mat-icon>remove</mat-icon>
          </button>
          <input class="hs-step-input" type="number" required name="numberofdays" [(ngModel)]="numberOfDays">
          <button type="button" class="hs-step-btn" (click)="numberOfDays = (numberOfDays || 0) + 1" aria-label="Increase">
            <mat-icon>add</mat-icon>
          </button>
        </div>
        <div class="hs-hint">Use a negative number to shift the schedule earlier.</div>
      </div>

      <!-- Partial shift toggle -->
      <div class="hs-field">
        <button type="button" class="hs-toggle-row" [class.on]="partialShift" (click)="partialShift = !partialShift">
          <span class="hs-toggle-text">
            <span class="hs-toggle-title">Partial shift</span>
            <span class="hs-toggle-desc">Only move tasks on or after a chosen start date</span>
          </span>
          <span class="hs-switch" [class.on]="partialShift"><span class="hs-knob"></span></span>
        </button>
      </div>

      <!-- Start date (partial only) -->
      @if (partialShift) {
        <div class="hs-field hs-field-last">
          <label class="hs-label">Start shifting from</label>
          <mat-form-field appearance="outline" class="hs-mat full-width">
            <input matInput [matDatepicker]="startDatePicker" placeholder="Choose a start date" name="startDate" [formControl]="startDate">
            <mat-datepicker-toggle matSuffix [for]="startDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #startDatePicker></mat-datepicker>
          </mat-form-field>
        </div>
      }
    </div>

    <!-- Right: live summary -->
    <aside class="hs-col-summary">
      <div class="hs-summary-head">
        <span class="hs-summary-title">Summary</span>
        <span class="hs-summary-badge" [class.back]="shiftDirection === 'earlier'">
          <mat-icon>{{shiftDirection === 'earlier' ? 'west' : 'east'}}</mat-icon>
          {{absDays}} day{{absDays === 1 ? '' : 's'}} {{shiftDirection}}
        </span>
      </div>

      <div class="hs-summary-card">
        <div class="hs-summary-row">
          <mat-icon>local_florist</mat-icon>
          <div class="hs-summary-copy">
            <span class="hs-summary-label">Affected</span>
            <span class="hs-summary-value">
              @if (selectedHarvests.length === 0) {
                No harvest selected
              } @else if (allSelected) {
                All {{selectedHarvests.length}} harvests
              } @else if (selectedHarvests.length === 1) {
                {{selectedHarvests[0].name}}
              } @else {
                {{selectedHarvests.length}} harvests
              }
            </span>
          </div>
        </div>

        <div class="hs-summary-row">
          <mat-icon>schedule</mat-icon>
          <div class="hs-summary-copy">
            <span class="hs-summary-label">Direction</span>
            <span class="hs-summary-value">
              Move {{absDays}} day{{absDays === 1 ? '' : 's'}} {{shiftDirection}}
            </span>
          </div>
        </div>

        <div class="hs-summary-row">
          <mat-icon>{{partialShift ? 'content_cut' : 'all_inclusive'}}</mat-icon>
          <div class="hs-summary-copy">
            <span class="hs-summary-label">Scope</span>
            <span class="hs-summary-value">
              @if (partialShift) {
                Tasks from {{startDate.value | date:'MMM d, y'}} onward
              } @else {
                Every task in the harvest
              }
            </span>
          </div>
        </div>
      </div>
    </aside>
  </form>

  <!-- Footer -->
  <div class="hs-footer">
    <span class="hs-footer-note">
      <mat-icon>info</mat-icon>
      Shifting reschedules existing tasks — it can't be undone in bulk.
    </span>
    <div class="hs-footer-actions">
      <button mat-button mat-dialog-close class="hs-btn-ghost" [disabled]="formSubmitted">Cancel</button>
      <button type="submit" form="shiftHarvest" class="hs-btn-primary" [disabled]="formSubmitted">
        @if (formSubmitted) {
          <mat-spinner diameter="20" strokeWidth="3"></mat-spinner>
        } @else {
          <span>Shift harvest</span>
          <mat-icon>arrow_forward</mat-icon>
        }
      </button>
    </div>
  </div>
</div>
